<template>
  <tbody class="p-datatable-tbody">
    <template v-if="!empty">
      <template v-for="(rowData, index) of value">
        <tr
          v-if="templates['group-header'] && rowGroupMode === 'subheader' && shouldRenderRowGroupHeader(value, rowData, index)"
          :key="getRowKey(rowData, index) + '_header'"
          class="p-rowgroup-header"
        >
          <template v-for="(col, i) of columns">
            <td
              v-if="expandableRowGroups && col.frozen && (groupRowsBy.indexOf(col.field) === 0 || i === 0)"
              :key="col.field"
            >
              <button
                v-if="expandableRowGroups"
                type="button"
                style="margin-right: 5px; height: 16px;"
                @click="onRowGroupToggle($event, rowData, groupRowsBy[0])"
              >
                <span :class="rowGroupTogglerIcon(rowData, 0)" />
              </button>
              <DTRowExpansionTemplate
                :template="templates['group-header']"
                :data="rowData"
                :index="index"
                :name="groupRowsBy[0]"
              />
            </td>
            <td
              v-else-if="col._params.isMeasure"
              :key="col.field"
              style="text-align: right"
            >
              <DTRowExpansionTemplate
                :template="templates['group-sum']"
                :data="rowData"
                :index="index"
                :name="col.field"
              />
            </td>
            <td
              v-else
              :key="col.field"
            >
              <DTRowExpansionTemplate
                :template="templates['group-cell']"
                :data="rowData"
                :index="index"
                :name="col.field"
              />
            </td>
          </template>
        </tr>
        <tr
          v-if="templates['group-header'] && rowGroupMode === 'subheader' && groupRowsBy.length > 1 && shouldRenderRowGroupHeader(value, rowData, index, 1)"
          :key="getRowKey(rowData, index) + '_subheader'"
          class="p-rowgroup-header"
        >
          <template v-for="(col, i) of columns">
            <td
              v-if="expandableRowGroups && col.frozen && i === groupRowsBy.length - 1"
              :key="col.field"
            >
              <button
                v-if="expandableRowGroups"
                type="button"
                style="margin-right: 5px;"
                @click="onRowGroupToggle($event, rowData, groupRowsBy[groupRowsBy.length - 1])"
              >
                <span :class="rowGroupTogglerIcon(rowData, 1)" />
              </button>
              <DTRowExpansionTemplate
                :template="templates['group-header']"
                :data="rowData"
                :index="index"
                :name="groupRowsBy[groupRowsBy.length - 1]"
              />
            </td>
            <td
              v-else-if="col._params.isMeasure"
              :key="col.field"
              style="text-align: right;"
            >
              <DTRowExpansionTemplate
                :template="templates['group-sum']"
                :data="rowData"
                :index="index"
                :name="col.field"
              />
            </td>
            <td
              v-else
              :key="col.field"
            >
              <DTRowExpansionTemplate
                :template="templates['group-cell']"
                :data="rowData"
                :index="index"
                :name="col.field"
              />
            </td>
          </template>
        </tr>
        <tr
          v-if="expandableRowGroups ? isRowGroupExpanded(rowData): true"
          :key="getRowKey(rowData, index)"
          :class="getRowClass(rowData)"
          :tabindex="selectionMode || contextMenu ? '0' : null"
          @click="onRowClick($event, rowData, index)"
          @contextmenu="onRowRightClick($event, rowData, index)"
          @touchend="onRowTouchEnd($event)"
          @keydown="onRowKeyDown($event, rowData, index)"
          @mousedown="onRowMouseDown($event)"
          @dragstart="onRowDragStart($event, index)"
          @dragover="onRowDragOver($event,index)"
          @dragleave="onRowDragLeave($event)"
          @dragend="onRowDragEnd($event)"
          @drop="onRowDrop($event)"
        >
          <template v-for="(col,i) of columns">
            <DTBodyCell
              v-if="shouldRenderBodyCell(value, col, index)"
              :key="col.columnKey||col.field||i"
              :rowData="rowData"
              :column="col"
              :index="index"
              :selected="isSelected(rowData)"
              :rowTogglerIcon="col.expander ? rowTogglerIcon(rowData): null"
              :rowspan="rowGroupMode === 'rowspan' ? calculateRowGroupSize(value, col, index) : null"
              :editMode="editMode"
              :editing="editMode === 'row' && isRowEditing(rowData)"
              @radio-change="onRadioChange($event)"
              @checkbox-change="onCheckboxChange($event)"
              @row-toggle="onRowToggle($event)"
              @cell-edit-init="onCellEditInit($event)"
              @cell-edit-complete="onCellEditComplete($event)"
              @cell-edit-cancel="onCellEditCancel($event)"
              @row-edit-init="onRowEditInit($event)"
              @row-edit-save="onRowEditSave($event)"
              @row-edit-cancel="onRowEditCancel($event)"
            />
            <td
              v-else
              :key="col.columnKey||col.field||i"
            >
              &nbsp;
            </td>
          </template>
        </tr>
        <tr
          v-if="templates['expansion'] && expandedRows && isRowExpanded(rowData)"
          :key="getRowKey(rowData, index) + '_expansion'"
          class="p-datatable-row-expansion"
        >
          <td :colspan="columns.length">
            <DTRowExpansionTemplate
              :template="templates['expansion']"
              :data="rowData"
              :index="index"
            />
          </td>
        </tr>
        <tr
          v-if="templates['groupfooter'] && rowGroupMode === 'subheader' && shouldRenderRowGroupFooter(value, rowData, index)"
          :key="getRowKey(rowData, index) + '_subfooter'"
          class="p-rowgroup-footer"
        >
          <DTRowExpansionTemplate
            :template="templates['groupfooter']"
            :data="rowData"
            :index="index"
          />
        </tr>
      </template>
    </template>
    <tr
      v-else
      class="p-datatable-emptymessage"
    >
      <td :colspan="columns.length">
        <DTSlotTemplate
          v-if="templates.empty && !loading"
          :template="templates.empty"
        />
        <DTSlotTemplate
          v-if="templates.loading && loading"
          :template="templates.loading"
        />
      </td>
    </tr>
  </tbody>
</template>

<script>
import ObjectUtils from 'primevue/utils/ObjectUtils';
import BodyCell from 'primevue/datatable/BodyCell.vue';
import MuiGridUtil from '../utils/MuiGridUtil';

const RowExpansionTemplate = {
  functional: true,
  props: {
    name: {
      type: String,
      default: null,
    },
    data: {
      type: null,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    template: {
      type: null,
      default: null,
    },
  },
  render(createElement, context) {
    const content = context.props.template({
      name: context.props.name,
      data: context.props.data,
      index: context.props.index,
    });
    return [content];
  },
};

const SlotTemplate = {
  functional: true,
  props: {
    template: {
      type: null,
      default: null,
    },
  },
  render(createElement, context) {
    const content = context.props.template();
    return [content];
  },
};

export default {
  components: {
    DTBodyCell: BodyCell,
    DTRowExpansionTemplate: RowExpansionTemplate,
    DTSlotTemplate: SlotTemplate,
  },
  props: {
    value: {
      type: Array,
      default: null,
    },
    columns: {
      type: null,
      default: null,
    },
    empty: {
      type: Boolean,
      default: false,
    },
    rowGroupMode: {
      type: String,
      default: null,
    },
    groupRowsBy: {
      type: [Array, String],
      default: null,
    },
    expandableRowGroups: {
      type: Boolean,
      default: false,
    },
    expandedRowGroups: {
      type: Array,
      default: null,
    },
    dataKey: {
      type: String,
      default: null,
    },
    expandedRowIcon: {
      type: String,
      default: null,
    },
    collapsedRowIcon: {
      type: String,
      default: null,
    },
    expandedRows: {
      type: Array,
      default: null,
    },
    expandedRowKeys: {
      type: null,
      default: null,
    },
    selection: {
      type: [Array, Object],
      default: null,
    },
    selectionKeys: {
      type: null,
      default: null,
    },
    selectionMode: {
      type: String,
      default: null,
    },
    contextMenu: {
      type: Boolean,
      default: false,
    },
    contextMenuSelection: {
      type: Object,
      default: null,
    },
    rowClass: {
      type: null,
      default: null,
    },
    editMode: {
      type: String,
      default: null,
    },
    compareSelectionBy: {
      type: String,
      default: 'deepEquals',
    },
    editingRows: {
      type: Array,
      default: null,
    },
    editingRowKeys: {
      type: null,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    templates: {
      type: null,
      default: null,
    },
  },
  methods: {
    shouldRenderRowGroupHeader(value, rowData, i, groupLevel = 0) {
      const thisVue = this;

      const groupList = this.groupRowsBy.slice(0, groupLevel + 1);
      const currentRowFieldData = MuiGridUtil.resolveDataOfFields(rowData, groupList);
      const prevRowData = value[i - 1];
      if (groupLevel > 0) {
        if (this.expandedRowGroups) {
          const isExpandedRow = this.expandedRowGroups.some(expandedRowGroup => {
            return expandedRowGroup[thisVue.groupRowsBy[groupLevel - 1]] === rowData[thisVue.groupRowsBy[groupLevel - 1]];
          });
          if (!isExpandedRow) {
            return false;
          }
        } else {
          return false;
        }
      }

      if (prevRowData) {
        const previousRowFieldData = MuiGridUtil.resolveDataOfFields(prevRowData, groupList);
        return !MuiGridUtil.isEqualObjectValue(currentRowFieldData, previousRowFieldData);
      }

      return true;
    },
    getRowKey(rowData, index) {
      return this.dataKey ? MuiGridUtil.resolveDataOfFields(rowData, this.dataKey) : index;
    },
    getRowClass(rowData) {
      const rowStyleClass = [];
      if (this.selectionMode) {
        rowStyleClass.push('p-selectable-row');
      }

      if (this.selection) {
        rowStyleClass.push({
          'p-highlight': this.isSelected(rowData),
        });
      }

      if (this.contextMenuSelection) {
        rowStyleClass.push({
          'p-highlight-contextmenu': this.isSelectedWithContextMenu(rowData),
        });
      }

      if (this.rowClass) {
        const rowClassValue = this.rowClass(rowData);

        if (rowClassValue) {
          rowStyleClass.push(rowClassValue);
        }
      }

      return rowStyleClass;
    },
    shouldRenderRowGroupFooter(value, rowData, i) {
      if (this.expandableRowGroups && !this.isRowGroupExpanded(rowData)) {
        return false;
      }

      const currentRowFieldData = MuiGridUtil.resolveDataOfFields(rowData, this.groupRowsBy);
      const nextRowData = value[i + 1];
      if (nextRowData) {
        const nextRowFieldData = MuiGridUtil.resolveDataOfFields(nextRowData, this.groupRowsBy);
        return !MuiGridUtil.isEqualObjectValue(currentRowFieldData, nextRowFieldData);
      }

      return true;
    },
    shouldRenderBodyCell(value, column, i) {
      if (this.rowGroupMode) {
        if (this.rowGroupMode === 'subheader') {
          return this.groupRowsBy.indexOf(column.field) < 0;
        }

        if (this.rowGroupMode === 'rowspan') {
          if (this.isGrouped(column)) {
            const prevRowData = value[i - 1];
            if (prevRowData) {
              const currentRowFieldData = MuiGridUtil.resolveDataOfFields(value[i], column.field);
              const previousRowFieldData = MuiGridUtil.resolveDataOfFields(prevRowData, column.field);
              return currentRowFieldData !== previousRowFieldData;
            }
            return true;
          }
          return true;
        }
      }
      return true;
    },
    calculateRowGroupSize(value, column, i) {
      let index = i;
      if (this.isGrouped(column)) {
        const currentRowFieldData = MuiGridUtil.resolveDataOfFields(value[index], column.field);
        let nextRowFieldData = currentRowFieldData;
        let groupRowSpan = 0;

        while (currentRowFieldData === nextRowFieldData) {
          groupRowSpan += 1;
          index += 1;
          const nextRowData = value[index];
          if (nextRowData) {
            nextRowFieldData = MuiGridUtil.resolveDataOfFields(nextRowData, column.field);
          } else {
            break;
          }
        }

        return groupRowSpan === 1 ? null : groupRowSpan;
      }
      return null;
    },
    rowTogglerIcon(rowData) {
      const icon = this.isRowExpanded(rowData) ? this.expandedRowIcon : this.collapsedRowIcon;
      return ['p-row-toggler-icon pi', icon];
    },
    rowGroupTogglerIcon(rowData, groupLevel) {
      const icon = this.isRowGroupExpanded(rowData, groupLevel) ? this.expandedRowIcon : this.collapsedRowIcon;
      return ['p-row-toggler-icon pi', icon];
    },
    isGrouped(column) {
      if (this.groupRowsBy) {
        if (Array.isArray(this.groupRowsBy)) {
          return this.groupRowsBy.indexOf(column.field) > -1;
        }
        return this.groupRowsBy === column.field;
      }
      return false;
    },
    isRowEditing(rowData) {
      if (rowData && this.editingRows) {
        if (this.dataKey) {
          return this.editingRowKeys ? this.editingRowKeys[MuiGridUtil.resolveDataOfFields(rowData, this.dataKey)] !== undefined : false;
        }
        return this.findIndex(rowData, this.editingRows) > -1;
      }

      return false;
    },
    isRowExpanded(rowData) {
      if (rowData && this.expandedRows) {
        if (this.dataKey) {
          return this.expandedRowKeys ? this.expandedRowKeys[MuiGridUtil.resolveDataOfFields(rowData, this.dataKey)] !== undefined : false;
        }
        return this.findIndex(rowData, this.expandedRows) > -1;
      }

      return false;
    },
    isRowGroupExpanded(rowData, groupLevel) {
      let { groupRowsBy } = this.$props;
      if (groupLevel != null) {
        groupRowsBy = groupRowsBy.slice(0, groupLevel + 1);
      }
      if (this.expandableRowGroups && this.expandedRowGroups) {
        const groupFieldValue = MuiGridUtil.resolveDataOfFields(rowData, groupRowsBy);
        return groupRowsBy.every(field => {
          return this.expandedRowGroups.some(group => {
            return group[field] === groupFieldValue[field];
          });
        });
      }
      return false;
    },
    isSelected(rowData) {
      if (rowData && this.selection) {
        if (this.dataKey) {
          return this.selectionKeys ? this.selectionKeys[MuiGridUtil.resolveDataOfFields(rowData, this.dataKey)] !== undefined : false;
        }

        if (Array.isArray(this.selection)) {
          return this.findIndexInSelection(rowData) > -1;
        }
        return this.equals(rowData, this.selection);
      }

      return false;
    },
    isSelectedWithContextMenu(rowData) {
      if (rowData && this.contextMenuSelection) {
        return this.equals(rowData, this.contextMenuSelection, this.dataKey);
      }

      return false;
    },
    findIndexInSelection(rowData) {
      return this.findIndex(rowData, this.selection);
    },
    findIndex(rowData, collection) {
      let index = -1;
      if (collection && collection.length) {
        for (let i = 0; i < collection.length; i += 1) {
          if (this.equals(rowData, collection[i])) {
            index = i;
            break;
          }
        }
      }

      return index;
    },
    equals(data1, data2) {
      return this.compareSelectionBy === 'equals' ? (data1 === data2) : ObjectUtils.equals(data1, data2, this.dataKey);
    },
    onRowGroupToggle(event, data, field) {
      let targetField = field;
      if (this.groupRowsBy.indexOf(field) < 0) {
        const [groupRowsBy] = this.groupRowsBy;
        targetField = groupRowsBy;
      }
      this.$emit('rowgroup-toggle', { originalEvent: event, data, field: targetField });
    },
    onRowClick(event, rowData, rowIndex) {
      this.$emit('row-click', { originalEvent: event, data: rowData, index: rowIndex });
    },
    onRowRightClick(event, rowData, rowIndex) {
      this.$emit('row-rightclick', { originalEvent: event, data: rowData, index: rowIndex });
    },
    onRowTouchEnd(event) {
      this.$emit('row-touchend', event);
    },
    onRowKeyDown(event, rowData, rowIndex) {
      this.$emit('row-keydown', { originalEvent: event, data: rowData, index: rowIndex });
    },
    onRowMouseDown(event) {
      this.$emit('row-mousedown', event);
    },
    onRowDragStart(event, rowIndex) {
      this.$emit('row-dragstart', { originalEvent: event, index: rowIndex });
    },
    onRowDragOver(event, rowIndex) {
      this.$emit('row-dragover', { originalEvent: event, index: rowIndex });
    },
    onRowDragLeave(event) {
      this.$emit('row-dragleave', event);
    },
    onRowDragEnd(event) {
      this.$emit('row-dragend', event);
    },
    onRowDrop(event) {
      this.$emit('row-drop', event);
    },
    onRowToggle(event) {
      this.$emit('row-toggle', event);
    },
    onRadioChange(event) {
      this.$emit('radio-change', event);
    },
    onCheckboxChange(event) {
      this.$emit('checkbox-change', event);
    },
    onCellEditInit(event) {
      this.$emit('cell-edit-init', event);
    },
    onCellEditComplete(event) {
      this.$emit('cell-edit-complete', event);
    },
    onCellEditCancel(event) {
      this.$emit('cell-edit-cancel', event);
    },
    onRowEditInit(event) {
      this.$emit('row-edit-init', event);
    },
    onRowEditSave(event) {
      this.$emit('row-edit-save', event);
    },
    onRowEditCancel(event) {
      this.$emit('row-edit-cancel', event);
    },
    getGroupSum(rowData, field, groupLevel = 0) {
      const filterGroups = this.groupRowsBy.slice(0, groupLevel + 1);
      const filterGroupObject = {};
      filterGroups.forEach(filterGroup => {
        filterGroupObject[filterGroup] = rowData[filterGroup];
      });

      let filteredData = this.value;
      Object.keys(filterGroupObject).forEach(filterGroupKey => {
        filteredData = filteredData.filter(row => row[filterGroupKey] === rowData[filterGroupKey]);
      });

      return filteredData.map(row => row[field]).reduce((a, b) => a + b, 0);
    },
    getNumberFormat(number) {
      return utils.numberFormat(number);
    },
  },
};
</script>
